import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CaretDown, CaretUp, ChatCircle, ArrowRight, ArrowLeft } from '@phosphor-icons/react';
import AppNav from '../../components/AppNav';
import AppFooter from '../../components/AppFooter';
import PageMeta from '../../components/PageMeta';
import JsonLd, { medicalConditionSchema, faqSchema, breadcrumbSchema } from '../../components/JsonLd';
import { getConditionBySlug, CONDITION_PAGES } from '../../data/conditionPages';

const BASE_URL = 'https://romyhealth.co';

export default function ConditionPage() {
  const { slug } = useParams<{ slug: string }>();
  const condition = slug ? getConditionBySlug(slug) : undefined;
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // ─── 404 state ──────────────────────────────────────────────────
  if (!condition) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
        <AppNav />
        <main className="pt-24 pb-16 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <div className="text-6xl mb-6">🔍</div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Condition Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              We could not find a condition page for "{slug}". Browse our supported conditions below.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {CONDITION_PAGES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/conditions/${c.slug}`}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-pink-50 dark:bg-pink-950 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800 hover:border-pink-400 dark:hover:border-pink-600 transition-all"
                >
                  {c.name}
                </Link>
              ))}
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 font-medium transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  // ─── Structured data ────────────────────────────────────────────
  const conditionUrl = `${BASE_URL}/conditions/${condition.slug}`;

  const structuredData = [
    medicalConditionSchema({
      name: condition.name,
      description: condition.overview.split('\n\n')[0],
      symptoms: condition.symptoms.map((s) => s.name),
      url: conditionUrl,
    }),
    faqSchema(condition.faqs),
    breadcrumbSchema([
      { name: 'Home', url: BASE_URL },
      { name: 'Conditions', url: `${BASE_URL}/conditions` },
      { name: condition.name, url: conditionUrl },
    ]),
  ];

  // Resolve related condition data
  const relatedConditions = condition.relatedConditions
    .map((relSlug) => CONDITION_PAGES.find((c) => c.slug === relSlug))
    .filter(Boolean) as typeof CONDITION_PAGES;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title={condition.metaTitle}
        description={condition.metaDescription}
        path={`/conditions/${condition.slug}`}
      />
      <JsonLd data={structuredData} />
      <AppNav />

      <main className="pt-16">
        {/* ─── Hero ──────────────────────────────────────────────── */}
        <section
          className={`bg-gradient-to-br from-${condition.gradientFrom} to-${condition.gradientTo} py-16 sm:py-20 lg:py-24`}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <div className="text-5xl sm:text-6xl mb-4">{condition.heroEmoji}</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {condition.name}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-6">
              {condition.tagline}
            </p>
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm sm:text-base font-medium">
              {condition.prevalence}
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* ─── Breadcrumbs ──────────────────────────────────────── */}
          <nav className="mb-8 text-sm text-gray-500 dark:text-gray-400" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 flex-wrap">
              <li>
                <Link to="/" className="hover:text-pink-600 dark:hover:text-pink-400 transition-colors">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <span className="text-gray-800 dark:text-gray-200 font-medium">{condition.name}</span>
              </li>
            </ol>
          </nav>

          {/* ─── Overview ─────────────────────────────────────────── */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6">
              Overview
            </h2>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              {condition.overview.split('\n\n').map((paragraph, i) => (
                <p
                  key={i}
                  className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 last:mb-0"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          {/* ─── Symptoms ─────────────────────────────────────────── */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6">
              Symptoms
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {condition.symptoms.map((symptom, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                    {symptom.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {symptom.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Treatments ───────────────────────────────────────── */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6">
              Treatment Options
            </h2>
            <div className="space-y-4">
              {condition.treatments.map((treatment, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-${condition.gradientFrom} to-${condition.gradientTo} flex items-center justify-center text-white text-sm font-bold`}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">
                        {treatment.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {treatment.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── FAQ Accordion ────────────────────────────────────── */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {condition.faqs.map((faq, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={i}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="font-semibold text-gray-800 dark:text-gray-100 pr-4">
                        {faq.question}
                      </span>
                      {isOpen ? (
                        <CaretUp size={20} className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
                      ) : (
                        <CaretDown size={20} className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 pt-0">
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ─── CTA Section ──────────────────────────────────────── */}
          <section className="mb-12 sm:mb-16">
            <div
              className={`bg-gradient-to-br from-${condition.gradientFrom} to-${condition.gradientTo} rounded-2xl p-8 sm:p-10 text-white text-center`}
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                Have Questions About {condition.name}?
              </h2>
              <p className="text-white/90 max-w-xl mx-auto mb-6">
                Romy can answer your questions, help you track symptoms, and prepare you for
                doctor visits with personalized, evidence-based guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to={`/chat?topic=${encodeURIComponent(condition.name)}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-800 font-semibold rounded-lg hover:bg-gray-50 transition-colors shadow-lg"
                >
                  <ChatCircle size={20} weight="fill" />
                  Ask Romy About {condition.name.split('(')[0].trim().split('/')[0].trim()}
                </Link>
                {condition.quizSlug && (
                  <Link
                    to={`/quiz?type=${condition.quizSlug}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/30 transition-colors border border-white/30"
                  >
                    Take the Self-Check Quiz
                    <ArrowRight size={18} weight="bold" />
                  </Link>
                )}
              </div>
            </div>
          </section>

          {/* ─── Related Conditions ───────────────────────────────── */}
          {relatedConditions.length > 0 && (
            <section className="mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6">
                Related Conditions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedConditions.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/conditions/${related.slug}`}
                    className="group bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-pink-300 dark:hover:border-pink-700 transition-all"
                  >
                    <div className="text-3xl mb-3">{related.heroEmoji}</div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">
                      {related.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {related.tagline}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* ─── Medical Disclaimer ───────────────────────────────── */}
          <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-2xl p-5 text-sm text-gray-600 dark:text-gray-400">
            <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Important Notice</p>
            <p>
              This information is for educational purposes only and is not a substitute for
              professional medical advice, diagnosis, or treatment. Always consult your healthcare
              provider for medical decisions specific to your situation.
            </p>
          </div>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
